import { CookiesService } from '@services/cookies.service';
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Config } from '@config/config.service';

@Injectable()
export class GenericHttp {
  accessError: Observable<boolean>;
  accessErrorSource: BehaviorSubject<boolean>;

  private apiUrl: string;
  private deafultContentType: any = {
    name: 'Content-Type',
    value: 'application/json;charset=utf-8'
  };
  private deafultAcceptType: any = {
    name: 'Accept',
    value: 'application/json'
  };
  private headers: object = {
    'Content-Type': 'application/json;charset=utf-8',
    Accept: 'application/json'
  };

  constructor(
    private http: HttpClient,
    private config: Config,
    private cookiesService: CookiesService
  ) {
    this.apiUrl = config.baseUrl;

    this.accessErrorSource = new BehaviorSubject<boolean>(false);
    this.accessError = this.accessErrorSource.asObservable();
  }

  get(
    path: string,
    queryParams: object = {},
    unautorizedErr?: boolean
  ): Observable<any> {
    let params = '';
    let fullPath = `${this.apiUrl}${path}`;

    Object.keys(queryParams).forEach(key => {
      params = params + key + '=' + queryParams[key] + '&';
    });

    if (params) {
      params = '?' + params.replace(/&$/, '');
      fullPath = fullPath + params;
    }

    const httpOptions = {
      headers: this.shapeHeaders()
    };

    return this.http.get(fullPath, httpOptions)
      .pipe(
        catchError((error) => this.handleError(error, unautorizedErr))
      );
  }

  post(path: string, body: object = {}, queryParams: object = {}): Observable<any> {
    let params = '';
    let fullPath = `${this.apiUrl}${path}`;

    Object.keys(queryParams).forEach(key => {
      params = params + key + '=' + queryParams[key] + '&';
    });

    if (params) {
      params = '?' + params.replace(/&$/, '');
      fullPath = fullPath + params;
    }

    const jsonBody: string = JSON.stringify(body);
    const httpOptions = {
      headers: this.shapeHeaders()
    };

    return this.http.post(fullPath, jsonBody, httpOptions)
      .pipe(
        catchError((error) => this.handleError(error, false))
      );
  }

  del(path: string, body: object = {}): Observable<any> {
    const fullPath = `${this.apiUrl}${path}`;

    const httpOptions = {
      headers: this.shapeHeaders()
    };

    return this.http.request('delete', fullPath, { body: body, headers: httpOptions.headers})
      .pipe(
        catchError((error) => this.handleError(error, false))
      );
  }

  put(path: string, body: object = {}): Observable<any> {
    const fullPath = `${this.apiUrl}${path}`;

    const jsonBody: string = JSON.stringify(body);
    const httpOptions = {
      headers: this.shapeHeaders()
    };

    return this.http.put(fullPath, jsonBody, httpOptions)

        .pipe(
          catchError((error) => this.handleError(error, false))
        );
  }

  patch(path: string, body: object = {}): Observable<any> {
    const fullPath = `${this.apiUrl}${path}`;
    const jsonBody: string = JSON.stringify(body);
    const httpOptions = {
      headers: this.shapeHeaders()
    };

    return this.http.patch(fullPath, jsonBody, httpOptions)
      .pipe(
        catchError((error) => this.handleError(error, false))
      );
  }

  getFile(path: string): Observable<any> {
    const fullPath = `${this.apiUrl}${path}`;

    const httpOptions = {
      responseType: 'blob' as 'json',
      headers: this.shapeHeaders()
    };

    return this.http.get(fullPath, httpOptions);
  }

  private shapeHeaders(): HttpHeaders {
    const accessToken: string = this.cookiesService
      .getCookie(this.config.accessTokenKey);
    let headers: HttpHeaders = new HttpHeaders()
      .set(this.deafultContentType.name, this.deafultContentType.value)
      .set(this.deafultAcceptType.name, this.deafultAcceptType.value);

    if (Boolean(accessToken)) {
      headers = headers.append('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  }

  private handleError(
    responseError: HttpResponse<any> | any,
    unautorizedErr?: boolean
  ): Observable<any> {

    if (responseError.status === 401 && !unautorizedErr) {
      this.accessErrorSource.next(true);
    }

    if (typeof responseError.error === 'object') {
      responseError.error.errorContentType = responseError.headers.get('content-type');
    }

    return throwError(responseError.error);
  }
}
